/**
 * Presentation helpers shared by the Insights dashboards.
 *
 * Both exist to stop numbers becoming unreadable: one keeps a log-shaped axis
 * from stacking its tick labels, the other keeps a long figure from being
 * clipped by a narrow card.
 */

const DECADES = [1e3, 1e4, 1e5, 1e6, 1e7, 1e8, 1e9, 1e10, 1e11, 1e12];

/**
 * The symlog constant used by the Insights bar charts, shared with
 * decadeTickValues so the scale and its ticks cannot drift apart.
 *
 * symlog is linear below this value and logarithmic above it, which is what
 * lets a 691M bar and a 400k bar appear on one axis without the small one
 * collapsing to a sliver.
 */
export const SYMLOG_CONSTANT = 100_000;

/**
 * Tick values for a symlog value axis, as decades up to the largest datum.
 *
 * d3's symlog scale inherits `linearish` ticks, so the default tick set is
 * spaced evenly in VALUE. On a log-shaped axis those upper ticks compress
 * toward the top until the labels overlap each other. Decades instead sit at
 * even spacing on the axis, so they stay legible at any magnitude.
 *
 * Deliberately does NOT append the exact maximum as a final tick: when the max
 * happens to sit just above a decade the two labels would collide, which is the
 * very problem this avoids. A slightly sparse axis beats an illegible one.
 *
 * `linearBelow` is the scale's symlog constant. Below it the scale is
 * approximately linear, so those decades are squashed against the baseline and
 * their labels land on top of the zero tick: with a 691M maximum, 0 / 1k / 10k
 * all fall within 7px of each other, while 100k upward are 45px+ apart. Decades
 * under the constant are therefore dropped — but only when the data actually
 * reaches above it. A dataset living entirely in the linear region spaces those
 * small decades out correctly, and should still get them.
 *
 * @param {number} max largest value plotted; 0 or negative yields just the zero tick
 * @param {number} [linearBelow=0] the scale's symlog constant; 0 disables the floor
 * @returns {number[]} ascending tick values, always starting at 0
 */
export const decadeTickValues = (max, linearBelow = 0) => {
  if (!Number.isFinite(max) || max <= 0) return [0];
  const floor = max > linearBelow ? linearBelow : 0;
  return [0, ...DECADES.filter((v) => v >= floor && v <= max)];
};

/**
 * Font size for a metric card's value, stepped down as the string gets longer.
 *
 * The cards can be as narrow as 150px and clip their overflow, so a long
 * figure such as "734,533,664" is cut off mid-number at the base size.
 *
 * @param {unknown} value the already-formatted value as rendered
 * @returns {string} a CSS font size
 */
export const metricValueFontSize = (value) => {
  const length = String(value ?? "").length;
  if (length > 13) return "20px";
  if (length > 10) return "24px";
  return "28px";
};
